import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, ReplaySubject, Subject, zip } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";
import { Widget } from "src/app/classes";
import { NewsfeedService } from "src/app/lib/newsfeed/services/newsfeed.service";
import { SessionService } from "src/app/lib/shared/services/session.service";
import { WidgetService } from "src/app/services/widget.service";
import { ConfirmDialogComponent, ConfirmDialogComponentData } from "../../../lib/shared/components/dialog/confirm-dialog/confirm-dialog.component";
import { SelectWidgettypeComponent } from "../select-widgettype/select-widgettype.component";
import { WidgetGridCell } from "../widget-globals";

@Injectable()
export class WidgetViewStandaloneComponentService {

    _showPlaceholder: boolean = false;
    set showPlaceholder(value: boolean) {
        if (!this.rightsMap.edit_dashboard) return;
        this._showPlaceholder = value;
    }
    get showPlaceholder(): boolean {
        return this._showPlaceholder;
    }

    rightsMap = {
        "edit_dashboard": false
    };

    private _addWidgetType = new ReplaySubject<number>(1);
    private _addWidgetCell = new ReplaySubject<WidgetGridCell>(1);

    private _viewMode: BehaviorSubject<number> = new BehaviorSubject(WidgetViewStandaloneModes.DEFAULT_MODE);
    viewMode$ = this._viewMode.asObservable();

    private _showControlBar: BehaviorSubject<boolean> = new BehaviorSubject(false);
    showControlBar$ = this._showControlBar.asObservable();

    private _subscriptions = [];

    constructor(
        private _widgetService: WidgetService,
        private _dialog: MatDialog,
        private _route: ActivatedRoute,
        private _snackBar: MatSnackBar,
        private _sessionService: SessionService,
        private _newsfeedService: NewsfeedService
    ) { }

    addWidget() {
        this.showDefaultView();
        this.showPlaceholder = true;
        this._showControlBar.next(false);
        this._dialog.open(SelectWidgettypeComponent, {
            panelClass: "no-padding-dialog-container"
        }).afterClosed().subscribe((result: number) => {
            if (result) this._addWidgetType.next(result);
        });
    }

    addWidgetAtPosition = (cell: WidgetGridCell) => this._addWidgetCell.next(cell);

    changeWidgetType(widget: Widget) {
        this._dialog.open(SelectWidgettypeComponent, {
            panelClass: 'no-padding-dialog-container',
            data: widget.widgettype
        })
            .afterClosed()
            .pipe(filter(x => typeof x === 'number'), switchMap(x => {
                widget.widgettype = x;
                return this._widgetService.updateWidget(widget);
            }))
            .subscribe((result: Widget) => location.reload())
    };

    deleteWidget(widget: Widget) {
        let subject = new Subject();
        this._widgetService.deleteWidget(widget.widgetid).subscribe({
            next: () => {
                subject.next();
            },
            error: (error) => {
                subject.error(error);
            },
            complete: () => subject.complete()
        });
        return subject.asObservable();
    }

    dispose() {
        for (let sub of this._subscriptions) sub.unsubscribe();
        this._subscriptions = [];
    }

    initRights = (soft: boolean = true) => {
        for (let index in this.rightsMap) this._sessionService.hasRight(index, soft).subscribe(result => this.rightsMap[index] = result);
    }

    setUp() {
        this.initRights();
        this._subscriptions.push(...[
            this._addWidgetType.subscribe(() => this.showAddWidgetView()),
            zip(this._addWidgetCell.asObservable(), this._addWidgetType.asObservable(), this._sessionService.user$.pipe(take(1)))
                .pipe(switchMap(([cell, widgetType, user]) => {
                    this.showDefaultView();
                    return this._widgetService.createWidget({
                        'widgeth': 1,
                        'widgetpage': 'db',
                        'widgettype': (widgetType as number),
                        'widgetw': 1,
                        'widgetx': (cell as WidgetGridCell).column - 1,
                        'widgety': (cell as WidgetGridCell).row - 1,
                        'widgetuser': user.id
                    } as Widget);
                }))
                .subscribe((widget: Widget) => {
                    location.reload();
                })
        ])
    }

    showAddWidgetView = () => this._viewMode.next(WidgetViewStandaloneModes.ADD_WIDGET_MODE);
    showDefaultView = () => this._viewMode.next(WidgetViewStandaloneModes.DEFAULT_MODE);
    toggleControlBar() {
        this.showControlBar$.pipe(take(1)).subscribe((value: boolean) => {
            this._showControlBar.next(!value);
            if(value){
                this._viewMode.next(WidgetViewStandaloneModes.DEFAULT_MODE);
                this._sessionService.getSettingByName(SessionService.globalSettingNames.SHOW_NEWSFEED).pipe(take(1)).subscribe((setting) => {
                    if(setting && setting.stgvalue === 'true') this._newsfeedService.setNewsfeedVisibility(true);
                });
            } else {
                this._viewMode.next(WidgetViewStandaloneModes.DRAG_AND_RESIZE_MODE);
                this._newsfeedService.setNewsfeedVisibility(false);
            }
        });
    }

    tryToAddWidgetAtPosition(cell: WidgetGridCell) {
        if (!this.rightsMap.edit_dashboard) return;
        if (cell.blocked) {
            this._dialog.open(ConfirmDialogComponent, {
                data: {
                    hasSecondButton: true,
                    headline: 'Widget anlegen',
                    text: 'Die gewählte Zelle ist scheinbar bereits belegt. Möchten Sie dennoch ein Widget an dieser Stelle anlegen?',
                    secondButtonTitle: 'Widget anlegen',
                    secondButtonCallback: () => this.addWidgetAtPosition(cell)
                } as ConfirmDialogComponentData
            })
        }
        else this.addWidgetAtPosition(cell);
    }

    WidgetViewStandaloneModes = WidgetViewStandaloneModes;
}

export const WidgetViewStandaloneModes = {
    DEFAULT_MODE: 0,
    DELETE_MODE: 1,
    DRAG_AND_RESIZE_MODE: 2,
    ADD_WIDGET_MODE: 3
}

